'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { Mail, Send, Sparkles, MessageSquare, ArrowRight, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { submitContactFormApi } from '@/lib/api/client';

export default function ContactPage() {
  const t = useTranslations('contactPage');

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');

  const [loading, setLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const targetEmail = 'raj.myinscribe@gmail.com';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSuccessMsg(null);
    setErrorMsg(null);

    if (!email || !message) {
      setErrorMsg(t('validationError'));
      return;
    }

    try {
      setLoading(true);
      const data = await submitContactFormApi({ name, email, subject, message });
      setSuccessMsg(data.message || t('successFallback'));
      setName('');
      setEmail('');
      setSubject('');
      setMessage('');
    } catch (err: any) {
      setErrorMsg(err.message || t('errorFallback'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto py-12 px-4 space-y-10">
      {/* Hero Section */}
      <div className="text-center space-y-3">
        <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#5ab4ff]/10 border border-[#5ab4ff]/30 text-[#5ab4ff] text-xs font-semibold uppercase tracking-wider">
          <Sparkles className="w-3.5 h-3.5 text-[#5ab4ff]" />
          <span>{t('eyebrow')}</span>
        </div>
        <h1 className="text-3xl sm:text-4xl font-['Lexend'] font-semibold text-white">{t('h1')}</h1>
        <p className="text-sm text-[rgba(220,236,255,0.75)] max-w-xl mx-auto leading-relaxed">
          {t('intro')}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
        {/* Direct Contact Info Sidebar */}
        <div className="bg-[rgba(13,22,45,0.7)] backdrop-blur-xl border border-[rgba(255,255,255,0.12)] rounded-3xl p-6 shadow-xl space-y-6">
          <div>
            <h2 className="text-lg font-['Lexend'] font-semibold text-white mb-1">{t('directHeading')}</h2>
            <p className="text-xs text-[rgba(220,236,255,0.7)]">{t('directSub')}</p>
          </div>

          <div className="p-4 bg-[rgba(5,10,36,0.6)] border border-[rgba(255,255,255,0.08)] rounded-xl space-y-2">
            <div className="flex items-center gap-2 text-xs font-semibold text-[#5ab4ff] uppercase tracking-wider">
              <Mail className="w-4 h-4 text-[#5ab4ff] shrink-0" />
              <span>{t('emailLabel')}</span>
            </div>
            <a
              href={`mailto:${targetEmail}`}
              className="text-sm font-medium text-[#5ab4ff] hover:text-[#8dcbff] hover:underline break-all block"
            >
              {targetEmail}
            </a>
            <p className="text-[11px] text-[rgba(220,236,255,0.65)] pt-1 border-t border-[rgba(255,255,255,0.08)]">
              {t('emailNote')}
            </p>
          </div>

          {/* Quick Cross-Links */}
          <div className="space-y-3 pt-4 border-t border-[rgba(255,255,255,0.1)]">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-[rgba(220,236,255,0.75)]">{t('quickLinksHeading')}</h3>
            <ul className="space-y-2 text-xs">
              <li>
                <Link href="/birth-chart-calculator" className="text-[#5ab4ff] hover:text-[#8dcbff] hover:underline flex items-center justify-between group">
                  <span>{t('link1')}</span>
                  <ArrowRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
              </li>
              <li>
                <Link href="/all-calculators" className="text-[#5ab4ff] hover:text-[#8dcbff] hover:underline flex items-center justify-between group">
                  <span>{t('link2')}</span>
                  <ArrowRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
              </li>
              <li>
                <Link href="/about" className="text-[#5ab4ff] hover:text-[#8dcbff] hover:underline flex items-center justify-between group">
                  <span>{t('link3')}</span>
                  <ArrowRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Contact Form */}
        <div className="lg:col-span-2 bg-[rgba(13,22,45,0.7)] backdrop-blur-xl border border-[rgba(255,255,255,0.12)] rounded-3xl p-6 sm:p-8 shadow-xl space-y-6">
          <div>
            <h2 className="text-xl font-['Lexend'] font-semibold text-white mb-1">{t('formHeading')}</h2>
            <p className="text-xs text-[rgba(220,236,255,0.7)]">
              {t('formSub')}
            </p>
          </div>

          {successMsg && (
            <div className="p-4 rounded-xl bg-[rgba(141,255,201,0.12)] border border-[rgba(141,255,201,0.3)] flex items-start gap-3 text-[#8dffc9] text-xs font-medium">
              <CheckCircle2 className="w-4 h-4 text-[#8dffc9] shrink-0 mt-0.5" />
              <span>{successMsg}</span>
            </div>
          )}

          {errorMsg && (
            <div className="p-4 rounded-xl bg-[rgba(255,155,176,0.12)] border border-[rgba(255,155,176,0.3)] flex items-start gap-3 text-[#ff9bb0] text-xs font-medium">
              <AlertCircle className="w-4 h-4 text-[#ff9bb0] shrink-0 mt-0.5" />
              <span>{errorMsg}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="contact-name" className="block text-xs font-semibold text-[rgba(220,236,255,0.85)] uppercase tracking-wider mb-2">
                  {t('nameLabel')}
                </label>
                <input
                  id="contact-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t('namePlaceholder')}
                  className="w-full h-12 bg-[rgba(5,10,36,0.6)] border border-[rgba(255,255,255,0.14)] rounded-xl px-4 text-sm text-white placeholder-[rgba(220,236,255,0.4)] focus:outline-none focus:border-[#5ab4ff] focus:ring-1 focus:ring-[#5ab4ff] transition-all"
                />
              </div>

              <div>
                <label htmlFor="contact-email" className="block text-xs font-semibold text-[rgba(220,236,255,0.85)] uppercase tracking-wider mb-2">
                  {t('emailFieldLabel')} <span className="text-[#5ab4ff]">*</span>
                </label>
                <input
                  id="contact-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('emailPlaceholder')}
                  className="w-full h-12 bg-[rgba(5,10,36,0.6)] border border-[rgba(255,255,255,0.14)] rounded-xl px-4 text-sm text-white placeholder-[rgba(220,236,255,0.4)] focus:outline-none focus:border-[#5ab4ff] focus:ring-1 focus:ring-[#5ab4ff] transition-all"
                />
              </div>
            </div>

            <div>
              <label htmlFor="contact-subject" className="block text-xs font-semibold text-[rgba(220,236,255,0.85)] uppercase tracking-wider mb-2">
                {t('subjectLabel')}
              </label>
              <input
                id="contact-subject"
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder={t('subjectPlaceholder')}
                className="w-full h-12 bg-[rgba(5,10,36,0.6)] border border-[rgba(255,255,255,0.14)] rounded-xl px-4 text-sm text-white placeholder-[rgba(220,236,255,0.4)] focus:outline-none focus:border-[#5ab4ff] focus:ring-1 focus:ring-[#5ab4ff] transition-all"
              />
            </div>

            <div>
              <label htmlFor="contact-message" className="block text-xs font-semibold text-[rgba(220,236,255,0.85)] uppercase tracking-wider mb-2">
                {t('messageLabel')} <span className="text-[#5ab4ff]">*</span>
              </label>
              <textarea
                id="contact-message"
                required
                rows={5}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={t('messagePlaceholder')}
                className="w-full bg-[rgba(5,10,36,0.6)] border border-[rgba(255,255,255,0.14)] rounded-xl px-4 py-3 text-sm text-white placeholder-[rgba(220,236,255,0.4)] focus:outline-none focus:border-[#5ab4ff] focus:ring-1 focus:ring-[#5ab4ff] transition-all"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full h-12 rounded-full bg-white hover:bg-[#e6edff] text-[#050a24] font-semibold text-sm shadow-md transition-all flex items-center justify-center gap-2 disabled:opacity-50 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#5ab4ff] active:scale-[0.98]"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>{t('sendingButton')}</span>
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  <span>{t('sendButton')}</span>
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
